import * as React from 'react';
import { Field, Icon } from '@teamsnap/teamsnap-ui';
import { debounce } from 'lodash';
import { useHistory } from 'react-router-dom';
import NavigationSearchServer, { queryValidForSearch } from './navigation-search-server';
import NativeClickListener from './native-click-listener';
import NavigationSearchSuggestions from './navigation-search-suggestions';

import api from '../../core/api';
import './navigation-search.scss';

type SearchResponse = {
  subtext: string;
  text: string;
  type: string;
  linkInfo: { memberId: number; organizationId: number };
  subLinks: {
    isReactRoute: boolean;
    defaultIcon: string;
    linkHost: string;
    linkUrl: string;
    subtext: string;
    text: string;
    type: string;
    linkInfo: {
      memberId: number;
      organizationId: number;
      programId: number;
      seasonId: number;
      seasonParticipantId: number;
      seasonRootDivisionId: number;
    };
  }[];
};

type Props = {
  searchContext: {
    organizationId: number;
    programId: number;
    seasonId: number;
    memberId: number;
  };
};

const NavigationSearch: React.FC<Props> = ({ searchContext }: Props) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [delayedSearchQuery, setDelayedSearchQuery] = React.useState('');
  const [isLoadingResults, setIsLoadingResults] = React.useState(false);
  const [isLoadingError, setIsLoadingError] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const [cursorIndex, setCursorIndex] = React.useState(-1);
  const [isLeavingTo, setIsLeavingTo] = React.useState(null);
  const [showHelpText, setShowHelpText] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);

  const [searchServer] = React.useState(new NavigationSearchServer());

  React.useMemo(
    () =>
      searchServer.getSuggestions(searchQuery, searchContext).then((result) => {
        setSuggestions(result[0] as any);
        setIsLoadingResults(result[1]);
        setIsLoadingError(result[2]);
        if (queryValidForSearch(searchQuery)) {
          setShowResults(true);
        }
      }),
    // disabling since it should not and will not work when calling when the servers internal state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delayedSearchQuery, searchContext]
  );

  const history = useHistory();

  React.useEffect(() => {
    if (suggestions?.length === 1) {
      setCursorIndex(0);
    }
  }, [suggestions]);

  React.useEffect(() => {
    if (isLeavingTo) {
      window.location.href = isLeavingTo;
    }
  }, [isLeavingTo]);

  const debouncedChangeHandler = React.useCallback(debounce(setDelayedSearchQuery, 200), []);

  const onChange = ({ target: { value } }) => {
    setShowHelpText(!searchQuery.trim());

    if (!queryValidForSearch(value)) {
      closeResults();
    }
    setSearchQuery(value);
    debouncedChangeHandler(value);
  };

  const onFocus = (e) => {
    setShowHelpText(!searchQuery.trim());

    if (queryValidForSearch(e.target.value)) {
      setShowResults(true);
    }
  };

  const closeResults = () => {
    setShowResults(false);
    setCursorIndex(-1);
  };

  const onKeyPress = (e) => {
    if (e.code === 'Enter') {
      const selectedItem = suggestions[cursorIndex];
      selectSuggestion(selectedItem);
    }
  };

  const onKeyUp = (e) => {
    if (e.code === 'ArrowDown') {
      setCursorIndex(Math.min(suggestions?.length - 1, cursorIndex + 1));
    } else if (e.code === 'ArrowUp') {
      setCursorIndex(Math.max(-1, cursorIndex - 1));
    }
  };

  const onKeyDown = (e) => {
    if (e.code === 'Tab') {
      closeResults();
    }
  };

  const selectSuggestion = (selectedItem) => {
    if (selectedItem) {
      closeResults();
      setSearchQuery('');
      document.getElementById('navSearchInput').blur();

      if (selectedItem.isReactRoute && window.location.origin === selectedItem?.linkHost) {
        history.push(selectedItem.linkUrl);
      } else {
        setIsLeavingTo(selectedItem.linkHost + selectedItem.linkUrl);
      }
    }
  };

  const icon = isLeavingTo ? (
    <Icon className="Icon" name="search" /> // <Icon className="Icon" name="spin" style={ { height: 16, width: 16 } } />
  ) : (
    <Icon className="Icon" name="search" />
  );

  return (
    <div id="NavigationSearch" className="Grid-cell u-flexAuto nav-search">
      <Field
        type="text"
        formFieldProps={{
          placeholder: 'Search for members, registrations, etc.',
          leftIcon: icon,
          inputProps: {
            onChange: onChange,
            onFocus: onFocus,
            onBlur: () => setShowHelpText(false),
            onKeyPress: onKeyPress,
            onKeyUp: onKeyUp,
            onKeyDown: onKeyDown,
            id: 'navSearchInput',
            value: searchQuery,
          },
        }}
        name="Sample"
      />

      <p className={`helpText ${showHelpText ? '' : 'u-hidden'}`}>
        Search for member names, reg forms, invoices, divisions, seasons, programs and more.
      </p>

      {showResults && (
        <NativeClickListener selector="#NavigationSearch" closeFn={closeResults}>
          <NavigationSearchSuggestions
            suggestions={suggestions}
            isLoading={isLoadingResults}
            isLoadingError={isLoadingError}
            cursorIndex={cursorIndex}
            onSelectFunction={selectSuggestion}
          />
        </NativeClickListener>
      )}
    </div>
  );
};

export default NavigationSearch;
